import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type {
  CreatePoliceDepartment,
  ReadPoliceDepartment,
  ReadPoliceDepartmentWithStats,
} from '@/services/api/generated/policeDepartmentsService';
import { usePoliceDepartmentsCreateMutation } from '@/services/api/police';

interface CreateDepartmentModalProps {
  onDepartmentCreated: (
    department: ReadPoliceDepartment | ReadPoliceDepartmentWithStats,
  ) => void;
  children?: React.ReactNode;
}

export const CreateDepartmentModal = ({
  onDepartmentCreated,
  children,
}: CreateDepartmentModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<CreatePoliceDepartment>({
    name: '',
    state_code: '',
    state_name: '',
  });

  const [createDepartment, { isLoading }] =
    usePoliceDepartmentsCreateMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = formData.name?.trim();
    const stateCode = formData.state_code?.trim();

    if (!name) {
      toast.error('Department name is required');
      return;
    }

    if (!stateCode) {
      toast.error('State code is required');
      return;
    }

    try {
      const newDepartment = await createDepartment({
        createPoliceDepartment: {
          name,
          state_code: stateCode.toUpperCase(),
        },
      }).unwrap();

      onDepartmentCreated(newDepartment);
      setIsOpen(false);
      setFormData({ name: '', state_code: '', state_name: '' });

      toast.success(
        `Created ${newDepartment.name}. We'll gather contact info automatically.`,
      );
    } catch (error) {
      console.error('Failed to create department:', error);
      toast.error('Failed to create department. Please try again.');
    }
  };

  const handleInputChange = (
    field: keyof CreatePoliceDepartment,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return !!(
      formData.name?.trim() &&
      formData.state_code?.trim() &&
      !isLoading
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center space-x-1"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Department</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Police Department</DialogTitle>
          <DialogDescription>
            Add a new police department with basic information. We&apos;ll
            automatically gather contact details and procedures in the
            background.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <Label
                htmlFor="dept-name"
                className="text-sm font-medium text-gray-700"
              >
                Department Name *
              </Label>
              <Input
                id="dept-name"
                value={formData.name ?? ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Los Angeles Police Department"
                className="mt-1"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label
                  htmlFor="state-code"
                  className="text-sm font-medium text-gray-700"
                >
                  State Code *
                </Label>
                <Input
                  id="state-code"
                  value={formData.state_code ?? ''}
                  onChange={(e) =>
                    handleInputChange('state_code', e.target.value)
                  }
                  placeholder="CA"
                  maxLength={2}
                  className="mt-1 uppercase"
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                💡 After creation, we&apos;ll automatically start gathering
                contact information, procedures, and forms for requesting
                records from this department.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid()}>
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Creating...</span>
                </div>
              ) : (
                'Create Department'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
